import React from 'react';
import {Tooltip} from '../Tooltip/Tooltip';
import { v4 as uuidv4 } from 'uuid';
import {ICheckbox, IRadio, ISwitch} from './types';

import styles from './Checkbox.module.css';
import flex from '../Flex/Flex.module.css';
import margin from '../Margin/Margin.module.css';

function Checkbox({
	children,
	type = 'checkbox',
	label = '',
	description = '',
	tooltip = '',
	tooltipHTML = '',
	name,
	value,
	checked,
	forcedChecked,
	disabled = false,
	onChange,
	size = 'm',
	fullWidth = false,
	hasLabel = true,
	theme = 'dark' 
}: ICheckbox): JSX.Element {

	const tooltipID = uuidv4();

	const change = (ev:any) => {
		if (typeof onChange === 'function') {
			console.log('Change', ev);
			onChange(ev);
		}
	};

	const handleCheckboxClick = (ev:any) => {
		if (forcedChecked !== undefined) {
			// Prevent 2 click propagations because of label
			ev.stopPropagation();
		}
	};

	const getIsChecked = () => {
		if (forcedChecked !== undefined) return forcedChecked;
		return checked;
	};

	const cssClass = [styles.wrapper];
	const childrenClass = [styles.label];
	const iconClass = [flex.flex_item_fix, flex.flex_item_align_start];
	const tooltipClass:string[] = [];
	const isChecked = getIsChecked();

	const sizeClass:any = {
		m: styles.wrapper_size_m,
		l: styles.wrapper_size_l,
		xl: styles.wrapper_size_xl,
	};

	if( disabled ){
		cssClass.push(styles.wrapper_disabled);
	}
	if (size && hasLabel && size in sizeClass) {
		cssClass.push(sizeClass[size]);
	}
	if( fullWidth ){
		childrenClass.push(flex.flex_item_full);
		cssClass.push(flex.flex);
		cssClass.push(styles.wrapper_vertical);
	}else{
		childrenClass.push(flex.flex_item_auto);
		cssClass.push(flex.flex_inline);
	}
	if (tooltip || tooltipHTML) {
		cssClass.push(fullWidth ? flex.flex_item_full : flex.flex_item_auto);
		tooltipClass.push(fullWidth ? flex.flex : flex.flex_inline);
	}

	if (theme === 'light') {
		cssClass.push(styles.wrapper_theme_light);
	}

	iconClass.push(type == 'radio' ? styles.radio : styles.checkbox);
	if (isChecked) {
		iconClass.push(type == 'radio' ? styles.radio_checked : styles.checkbox_checked);
	} else {
		iconClass.push(type == 'radio' ? styles.radio_unchecked : styles.checkbox_unchecked);
	}

	const inner = (
		<label className={cssClass.join(' ')}>
			{forcedChecked !== undefined ?
				<input
					type={type}
					name={name}
					value={value}
					checked={forcedChecked ? true : false}
					onChange={(ev) => change(ev)}
					onClick={(ev) => handleCheckboxClick(ev)}
					disabled={disabled}
					readOnly={true}
					className={type == 'radio' ? styles.input_radio : styles.input_checkbox}
				/>
				:
				<input
					type={type}
					name={name}
					value={value}
					onChange={(ev) => change(ev)}
					defaultChecked={checked ? true : false}
					disabled={disabled}
					className={type == 'radio' ? styles.input_radio : styles.input_checkbox}
				/>
			}
			<div className={iconClass.join(' ')}></div>
			{hasLabel &&
				<div className={childrenClass.join(' ')}>
					{label || children}
					{description &&
						<div className={styles.description}>{description}</div>
					}
				</div>
			}
		</label>
	);

	if (tooltip || tooltipHTML) {
		return (
			<span className={tooltipClass.join(' ')}>
				{inner}
				<span className={styles.label_tooltip + ' ' + flex.flex_item_fix + ' ' + flex.flex_item_align_start}>
					<a
						className="icon_btn s v_al_bl"
						data-tooltip-content={tooltipHTML ? 'a' : tooltip}
						data-tooltip-id={tooltipID}>
						<i className="fas fa-info-circle"></i>
					</a>
					<Tooltip
						html={tooltipHTML}
						id={tooltipID}
					/>
				</span>
			</span>
		);
	} else {
		return inner;
	}
}


function Radio(props: IRadio): JSX.Element {
	return (
		<Checkbox {...props} type="radio">
			{props.children}
		</Checkbox>
	);
}


function Switch({
	children,
	label,
	name,
	value,
	checked,
	onChange,
	disabled = false,
	forcedChecked,
	position = 'left',
	size = 'm',
	align = 'top',
	verticalSize = 'm',
	noMargin = false
}: ISwitch): JSX.Element {

	const change = (ev:any) => {
		if (typeof onChange === 'function') {
			onChange(ev);
		}
	};

	const getIsChecked = () => {
		if (forcedChecked !== undefined) return forcedChecked;
		return checked;
	};

	const labelClass = [styles.wrapper];
	const switchClass = [styles.switch, flex.flex_item_fix];
	const isChecked = getIsChecked();

	const sizeClass:any = {
		m: styles.switch_size_m,
		s: styles.switch_size_s,
		xs: styles.switch_size_xs,
	};
	const paddingClass:any = {
		m: styles.wrapper_padding_m,
		l: styles.wrapper_padding_l,
		xl: styles.wrapper_padding_xl,
	};

	if (isChecked) {
		switchClass.push(styles.switch_checked);
	}

	if( align == 'top' ){
		switchClass.push(flex.flex_item_align_start);
	}

	if (verticalSize && verticalSize in paddingClass) {
		labelClass.push(paddingClass[verticalSize]);
	}

	if (size && size in sizeClass) {
		switchClass.push(sizeClass[size]);
	}

	if( position == 'right' ){
		labelClass.push(flex.flex);
	}else{
		labelClass.push(flex.flex_inline);
	}

	if( disabled ){
		labelClass.push(styles.wrapper_disabled);
	}

	if (noMargin) {
		labelClass.push(styles.wrapper_no_margin);
	}

	const input = (
		<>
			{forcedChecked !== undefined ?
				<input
					type="checkbox"
					name={name}
					value={value}
					onChange={(ev) => change(ev)}
					checked={forcedChecked ? true : false}
					disabled={disabled}
					readOnly={true}
					className={styles.input_switch}
				/>
				:
				<input
					type="checkbox"
					name={name}
					value={value}
					onChange={(ev) => change(ev)}
					defaultChecked={checked ? true : false}
					disabled={disabled}
					className={styles.input_switch}
				/>
			}
		</>
	);

	return (
		<label className={labelClass.join(' ')} onClick={(e) => e.stopPropagation() }>
			{position == 'right' && (children || label) &&
                <span className={styles.label + ' ' + flex.flex_item_full + ' ' + margin.r_10}>{label || children}</span>
			}
			{input}
			<span className={switchClass.join(' ')}></span>
			{position == 'left' && (children || label) &&
                <span className={styles.label + ' ' + flex.flex_item_auto}>{label || children}</span>
			}
		</label>
	);
}

export {
	Checkbox,
	Radio,
	Switch
};
